import { Injectable } from '@angular/core';
import { StorageValue } from '../../../models/utils/common.interfaces';
import { LoggingService } from '../logging/logging.service';
import { StorageHelperService } from './storage-helper.service';

/**
 * Service for storing data as key value pairs.
 */
@Injectable({
  providedIn: 'root'
})
export class StorageService extends StorageHelperService {
  /**
   * The constructor for the storage service.
   */
  constructor(private logging: LoggingService) {
    super();
  }

  /**
   * Get item from localStorage and parse it.
   */
  get(key: string, bypass = false, useSessionStorage = false) {
    try {
      if (useSessionStorage) {
        return JSON.parse(sessionStorage.getItem(key) as string);
      }
      const result = JSON.parse(localStorage.getItem(key) as string);
      if (this.checkBypassOrTimeoutValue(result, bypass)) {
        return result.value;
      }
      const cookieResult = this.getCookie(key);
      return this.returnCookieValueOrNull(cookieResult);
    } catch (e: Error | unknown) {
      this.logging.log(this.checkErrorInstance(e));
      const cookieValue = this.getCookie(key);
      return this.returnCookieValueOrNull(cookieValue);
    }
  }

  checkErrorInstance(e: Error | unknown): string {
    return e instanceof Error ? e.toString() : String(e);
  }

  checkBypassOrTimeoutValue(
    result: { value: StorageValue; timeout: number } | null,
    bypass: boolean
  ): boolean {
    return !!(
      result &&
      (bypass || result.timeout >= Date.now() || result.timeout === 0)
    );
  }

  returnCookieValueOrNull(cookieResult: string) {
    return cookieResult ? cookieResult : null;
  }

  checkByPassAndTimeoutValue(
    result: { value: StorageValue; timeout: number } | null,
    bypass: boolean
  ): boolean {
    return !!(
      result &&
      (bypass || result.timeout >= new Date().getTime() || result.timeout === 0)
    );
  }

  getStorageValue(useSessionStorage: boolean, key: string) {
    if (useSessionStorage) {
      return window.JSON.parse(window.sessionStorage.getItem(key) as string);
    } else {
      return window.JSON.parse(window.localStorage.getItem(key) as string);
    }
  }

  /**
   * Stringify data and save to localStorage.
   */
  set(
    key: string,
    value: StorageValue,
    timeout = 0,
    useSessionStorage = false
  ) {
    try {
      if (useSessionStorage) {
        window.sessionStorage.setItem(key, window.JSON.stringify(value));
      } else {
        const result = {
          value,
          timeout
        };

        if (timeout) {
          const timeoutDateObject: Date = new Date();
          timeoutDateObject.setMilliseconds(
            new Date().getMilliseconds() + timeout
          );
          result.timeout = timeoutDateObject.getTime();
        }
        window.localStorage.setItem(key, window.JSON.stringify(result));
      }
    } catch (e) {
      this.setCookie(key, value, '/', window.location.hostname, timeout);
      this.logging.log(e as string);
    }
  }

  /**
   * Remove item from localStorage.
   */
  remove(key: string, useSessionStorage = false) {
    try {
      if (useSessionStorage) {
        window.sessionStorage.removeItem(key);
      } else {
        window.localStorage.removeItem(key);
      }
    } catch (exception) {
      this.logging.log(exception as string);
      this.unsetCookie(key);
    }
  }

  /**
   * Parses Cookie value
   */
  getParsedCookie<T>(cookieName: string): T {
    try {
      return JSON.parse(this.getCookie(cookieName)) as T;
    } catch (exception) {
      return exception as T;
    }
  }
}
